import React, { Component } from 'react';
import { func, number, object, string } from 'prop-types';
import classNames from 'classnames';

import { intlShape } from '../../../util/reactIntl';
import {
  getStartHours,
  getEndHours,
  isInRange,
  isSameDate,
  timeOfDayFromLocalToTimeZone,
  isDateSameOrAfter,
  findNextBoundary,
  timestampToDate,
  formatDateIntoPartials,
  getStartOf,
  START_DATE,
} from '../../../util/dates';
import { propTypes } from '../../../util/types';
import { FieldSelect } from '../../../components';

import css from './FieldDateAndTimeInput.module.css';
import CustomRangeInput from '../CustomRangeInput/CustomRangeInput';
import { ENUMS } from '../../../util/enums';

const TODAY = new Date();

const getAvailableStartTimes = (intl, timeZone, bookingStart, timeSlotsOnSelectedDate) => {
  if (timeSlotsOnSelectedDate.length === 0 || !timeSlotsOnSelectedDate[0] || !bookingStart) {
    return [];
  }
  const bookingStartDate = getStartOf(bookingStart, 'day', timeZone);

  const allHours = timeSlotsOnSelectedDate.reduce((availableHours, t) => {
    const startDate = t.attributes.start;
    const endDate = t.attributes.end;
    const nextDate = getStartOf(bookingStartDate, 'day', timeZone, 1, 'days');

    const startLimit = isDateSameOrAfter(bookingStartDate, startDate)
      ? bookingStartDate
      : startDate;

    const endLimit = isDateSameOrAfter(endDate, nextDate) ? nextDate : endDate;
    const hours = getStartHours(startLimit, endLimit, timeZone, intl);
    return availableHours.concat(hours);
  }, []);
  return allHours;
};

const getAvailableEndTimes = (
  intl,
  timeZone,
  bookingStartTime,
  bookingEndDate,
  selectedTimeSlot
) => {
  if (!selectedTimeSlot || !selectedTimeSlot.attributes || !bookingEndDate || !bookingStartTime) {
    return [];
  }

  const endDate = selectedTimeSlot.attributes.end;
  const bookingStartTimeAsDate = timestampToDate(bookingStartTime);

  const dayAfterBookingEnd = getStartOf(bookingEndDate, 'day', timeZone, 1, 'days');
  const dayAfterBookingStart = getStartOf(bookingStartTimeAsDate, 'day', timeZone, 1, 'days');
  const startOfEndDay = getStartOf(bookingEndDate, 'day', timeZone);

  let startLimit;
  let endLimit;

  if (!isDateSameOrAfter(startOfEndDay, bookingStartTimeAsDate)) {
    startLimit = bookingStartTimeAsDate;
    endLimit = isDateSameOrAfter(dayAfterBookingStart, endDate) ? endDate : dayAfterBookingStart;
  } else {
    startLimit = isDateSameOrAfter(bookingStartTimeAsDate, startOfEndDay)
      ? bookingStartTimeAsDate
      : startOfEndDay;

    endLimit = isSameDate(getStartOf(endDate, 'day', timeZone), startOfEndDay)
      ? endDate
      : dayAfterBookingEnd;
  }

  return getEndHours(startLimit, endLimit, timeZone, intl);
};

const getTimeSlots = (timeSlots, date, timeZone) => {
  return timeSlots && timeSlots[0]
    ? timeSlots.filter(t => {
        return isInRange(date, t.attributes.start, t.attributes.end, 'day', timeZone);
      })
    : [];
};

const getAllTimeValues = (
  intl,
  timeZone,
  timeSlots,
  startDate,
  selectedStartTime,
  selectedEndDate
) => {
  const startTimes = selectedStartTime
    ? []
    : getAvailableStartTimes(
        intl,
        timeZone,
        startDate,
        getTimeSlots(timeSlots, startDate, timeZone)
      );

  const startTime = selectedStartTime
    ? selectedStartTime
    : startTimes.length > 0 && startTimes[0] && startTimes[0].timestamp
    ? startTimes[0].timestamp.toString()
    : null;

  const startTimeAsDate = startTime ? timestampToDate(startTime) : null;

  const endDate = selectedEndDate
    ? selectedEndDate
    : startTimeAsDate
    ? new Date(findNextBoundary(startTimeAsDate, 'hour', timeZone).getTime() - 1)
    : null;

  const selectedTimeSlot = getTimeSlots(timeSlots, startDate, timeZone).find(t =>
    isInRange(startTimeAsDate, t.attributes.start, t.attributes.end, 'minute', timeZone)
  );

  const endTimes = getAvailableEndTimes(intl, timeZone, startTime, endDate, selectedTimeSlot);

  const endTime =
    endTimes.length > 0 && endTimes[0] && endTimes[0].timestamp
      ? endTimes[0].timestamp.toString()
      : null;

  return { startTime, endDate, endTime, selectedTimeSlot };
};

const getMonthlyTimeSlots = monthlyTimeSlots => {
  if (!monthlyTimeSlots || Object.keys(monthlyTimeSlots).length === 0) {
    return [];
  }
  return Object.keys(monthlyTimeSlots).reduce((allTimeSlots, monthId) => {
    const monthData = monthlyTimeSlots[monthId];

    if (monthData && Array.isArray(monthData.timeSlots)) {
      allTimeSlots = allTimeSlots.concat(monthData.timeSlots);
    }

    return allTimeSlots;
  }, []);
};

class FieldDateAndTimeInput extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentMonth: getStartOf(TODAY, 'month', props.timeZone),
      focusedInput: START_DATE,
    };

    this.onBookingStartDateChange = this.onBookingStartDateChange.bind(this);
    this.onBookingStartTimeChange = this.onBookingStartTimeChange.bind(this);
    this.onBookingEndDateChange = this.onBookingEndDateChange.bind(this);
  }

  onBookingStartDateChange = (updateDay, index) => {
    const { monthlyTimeSlots, timeZone, intl, form, values } = this.props;
    const date = updateDay?._d;
    const startDate = timeOfDayFromLocalToTimeZone(date, timeZone);
    const timeSlots = getMonthlyTimeSlots(monthlyTimeSlots, this.state.currentMonth, timeZone);
    const timeSlotsOnSelectedDate = getTimeSlots(timeSlots, startDate, timeZone);
    const { startTime, endDate, endTime } = getAllTimeValues(
      intl,
      timeZone,
      timeSlotsOnSelectedDate,
      startDate
    );

    form.batch(() => {
      if (index == -1) {
        const updatedBookingDates = values.selectedBookingDates.filter(booking => {
          const bookingDate = new Date(booking.bookingStartDate.date).getTime(); 
          const targetDate = new Date(date).getTime(); 
        
          return bookingDate !== targetDate;
        });
        form.change('selectedBookingDates', updatedBookingDates);
      } else {
        form.change(`selectedBookingDates[${index}].bookingStartDate`, { date: startDate });
        form.change(`selectedBookingDates[${index}].bookingStartTime`, startTime);
        form.change(`selectedBookingDates[${index}].bookingEndDate`, { date: endDate });
        form.change(`selectedBookingDates[${index}].bookingEndTime`, endTime);
      }
    });
  };

  onBookingStartTimeChange = (value, index) => {
    const { monthlyTimeSlots, timeZone, intl, form, values } = this.props;
    const timeSlots = getMonthlyTimeSlots(monthlyTimeSlots, this.state.currentMonth, timeZone);
    const startDate =
      values?.selectedBookingDates && values?.selectedBookingDates[index]?.bookingStartDate?.date;
    const timeSlotsOnSelectedDate = getTimeSlots(timeSlots, startDate, timeZone);
    const { endDate, endTime } = getAllTimeValues(
      intl,
      timeZone,
      timeSlotsOnSelectedDate,
      startDate,
      value
    );
    form.batch(() => {
      form.change(`selectedBookingDates[${index}].bookingStartTime`, value);
      form.change(`selectedBookingDates[${index}].bookingEndDate`, { date: endDate });
      form.change(`selectedBookingDates[${index}].bookingEndTime`, endTime);
    });
  };

  onBookingEndDateChange = (value, index) => {
    const endDate = value && timestampToDate(value);
    const { form } = this.props;

    form.batch(() => {
    form.change(`selectedBookingDates[${index}].bookingEndTime`, value);
    form.change(`selectedBookingDates[${index}].bookingEndDate`, { date: endDate });
    })
  };

  render() {
    const {
      rootClassName,
      className,
      formId,
      values,
      monthlyTimeSlots,
      timeZone,
      intl,
      allTimeSlots,
      form,
      selectedDates,
      setSelectedDates,
      focusedInput,
      setFocusedInput,
    } = this.props;
   
    const classes = classNames(rootClassName || css.root, className);
    const timeSlotsOnSelectedMonth = getMonthlyTimeSlots(
      monthlyTimeSlots,
      this.state.currentMonth,
      timeZone
    );
    
    return (
      <div className={classes}>
        <div className={css.formRow}>
          <div className={classNames(css.field, css.startDate)}>
            <CustomRangeInput
              setSelectedDates={setSelectedDates}
              selectedDates={selectedDates}
              form={form}
              focusedInput={focusedInput}
              setFocusedInput={setFocusedInput}
              className={css.customCalendar}
              allTimeSlots={allTimeSlots}
              isBookingTimeForm={true}
              values={values}
              onBookingStartDateChange={this.onBookingStartDateChange}
            />
          </div>
        </div>

        {selectedDates &&
          selectedDates.map((date, index) => {
            const formatedDate = date.format(ENUMS.DEFAULT_MONTH_DATE_FORMAT);
            const bookingStartDate =
              values?.selectedBookingDates && values?.selectedBookingDates[index]?.bookingStartTime
                ? timestampToDate(values?.selectedBookingDates[index]?.bookingStartTime)
                : timeOfDayFromLocalToTimeZone(date, timeZone);

            const timeSlotsOnSelectedDate = getTimeSlots(
              timeSlotsOnSelectedMonth,
              bookingStartDate,
              timeZone
            );

            const availableStartTimes = getAvailableStartTimes(
              intl,
              timeZone,
              bookingStartDate,
              timeSlotsOnSelectedDate
            );

            const firstAvailableStartTime =
              availableStartTimes.length > 0 &&
              availableStartTimes[0] &&
              availableStartTimes[0].timestamp
                ? availableStartTimes[0].timestamp
                : null;
            const selectedTimeSlot = timeSlotsOnSelectedDate.find(t =>
              isInRange(bookingStartDate, t.attributes.start, t.attributes.end, 'minute', timeZone)
            );

            const { startTime, endDate } = getAllTimeValues(
              intl,
              timeZone,
              timeSlotsOnSelectedDate,
              bookingStartDate,
              (values?.selectedBookingDates &&
                values?.selectedBookingDates[index]?.bookingStartTime) ||
                firstAvailableStartTime,
              (values?.selectedBookingDates &&
                values?.selectedBookingDates[index]?.bookingEndDate?.date) ||
                bookingStartDate
            );

            const availableEndTimes = getAvailableEndTimes(
              intl,
              timeZone,
              (values?.selectedBookingDates &&
                values?.selectedBookingDates[index]?.bookingStartTime) ||
                startTime,
              (values?.selectedBookingDates &&
                values?.selectedBookingDates[index]?.bookingEndDate?.date) ||
                endDate,
              selectedTimeSlot
            );

            const nextBoundary = findNextBoundary(TODAY, 'hour', timeZone);
            let placeholderTime = '08:00';
            try {
              placeholderTime = formatDateIntoPartials(nextBoundary, intl, { timeZone })?.time;
            } catch (error) {
              // No need to handle error
            }

            const bookingEndTimeAvailable =
              (bookingStartDate &&
                values?.selectedBookingDates &&
                values?.selectedBookingDates[index]?.bookingStartTime) ||
              startTime;

            return (
              <div key={index} className={classNames(css.formRow, css.availableSlotsSec)}>
                <div className={css.dateField}>
                  <label htmlFor="Date"> Date </label>
                  <span> {formatedDate}</span>
                </div>
                <div className={css.field}>
                  <FieldSelect
                    name={`selectedBookingDates[${index}].bookingStartTime`}
                    id={
                      formId
                        ? `${formId}.selectedBookingDates[${index}].bookingStartTime`
                        : `selectedBookingDates[${index}].bookingStartTime`
                    }
                    className={bookingStartDate ? css.fieldSelect : css.fieldSelectDisabled}
                    selectClassName={bookingStartDate ? css.select : css.selectDisabled}
                    label={intl.formatMessage({ id: 'FieldDateAndTimeInput.startTime' })}
                    disabled={!bookingStartDate}
                    onChange={e => this.onBookingStartTimeChange(e, index)}
                  >
                    {bookingStartDate ? (
                      availableStartTimes.map(p => (
                        <option key={p.timestamp} value={p.timestamp}>
                          {p.timeOfDay}
                        </option>
                      ))
                    ) : (
                      <option>{placeholderTime}</option>
                    )}
                  </FieldSelect>
                </div>

                <div className={bookingStartDate ? css.lineBetween : css.lineBetweenDisabled}>
                  -
                </div>

                <div className={css.field}>
                  <FieldSelect
                    name={`selectedBookingDates[${index}].bookingEndTime`}
                    id={
                      formId
                        ? `${formId}.selectedBookingDates[${index}].bookingEndTime`
                        : `selectedBookingDates[${index}].bookingEndTime`
                    }
                    className={bookingStartDate ? css.fieldSelect : css.fieldSelectDisabled}
                    selectClassName={bookingStartDate ? css.select : css.selectDisabled}
                    label={intl.formatMessage({ id: 'FieldDateAndTimeInput.endTime' })}
                    disabled={!bookingEndTimeAvailable}
                    onChange={e => this.onBookingEndDateChange(e, index)}
                  >
                    {bookingEndTimeAvailable ? (
                      availableEndTimes.map(p => (
                        <option key={p.timestamp} value={p.timestamp}>
                          {p.timeOfDay === '00:00' ? '24:00' : p.timeOfDay}
                        </option>
                      ))
                    ) : (
                      <option>{placeholderTime}</option>
                    )}
                  </FieldSelect>
                </div>
              </div>
            );
          })}
      </div>
    );
  }
}

FieldDateAndTimeInput.defaultProps = {
  rootClassName: null,
  className: null,
  startDateInputProps: null,
  endDateInputProps: null,
  startTimeInputProps: null,
  endTimeInputProps: null,
  listingId: null,
  monthlyTimeSlots: null,
  timeZone: null,
};

FieldDateAndTimeInput.propTypes = {
  rootClassName: string,
  className: string,
  formId: string,
  startDateInputProps: object,
  endDateInputProps: object,
  startTimeInputProps: object,
  endTimeInputProps: object,
  form: object.isRequired,
  values: object.isRequired,
  listingId: propTypes.uuid,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,
  timeZone: string,
  dayCountAvailableForBooking: number,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default FieldDateAndTimeInput;
